import { useCallback, useContext, useMemo, useState } from 'react'

import { useMutation } from '@tanstack/react-query'
import { Scope } from '@unform/core'
import { Alert } from '@vtex/shoreline'
import { t } from 'i18next'
import moment from 'moment'

import { Button } from '~/components/Buttons'
import { FeedbackContext } from '~/components/feedback'
import Form from '~/components/unform/Form'
import NameAndTimeInterval from '~/components/unform/FormGroup/NameAndTimeInterval'
import { handleValidationsErrorsAPI, history } from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import { useErrorApiAlert } from '~/hooks/useErrorApiAlert'
import { AdditionalAssets } from '~/modules/retailMedia/components/CampaignForm/elements/AdditionalAssets'
import AdvancedSettings from '~/modules/retailMedia/components/CampaignForm/elements/Advanced'
import { Banner } from '~/modules/retailMedia/components/CampaignForm/elements/Banner'
import Budget from '~/modules/retailMedia/components/CampaignForm/elements/Budget'
import CampaignDestinationUrl from '~/modules/retailMedia/components/CampaignForm/elements/CampaignDestinationUrl'
import CampaignTargeting from '~/modules/retailMedia/components/CampaignForm/elements/CampaignTargeting'
import DailyCostScope from '~/modules/retailMedia/components/CampaignForm/elements/DailyCostScope'
import { useCampaignForm } from '~/modules/retailMedia/components/CampaignForm/hooks/useCampaignForm'
import { usePrepareCampaignCreateBody } from '~/modules/retailMedia/components/CampaignForm/hooks/usePrepareCampaignCreateBody'
import { useSubmitErrorMessage } from '~/modules/retailMedia/components/CampaignForm/hooks/useSubmitErrorMessage'
import ProductsPanel from '~/modules/retailMedia/components/ProductsPanel'
import {
  createCampaignKey,
  createCampaignService
} from '~/modules/retailMedia/services/campaign'
import { useAppSelector } from '~/store/hooks'

import CampaignType from '../../../../elements/CampaignType'
import Subpublishers from '../../../../elements/Subpublishers'
import { applyPreparedCampaignBody } from '../../../../helpers/applyPreparedCampaignBody'
import { withDefaultAgeRestrictedSetting } from '../../../helpers'
import { DTOCreateBannerVideoCampaign } from './dto'

type CampaignTargetingState =
  | undefined
  | {
      targeting: RetailMediaCampaignSegmentation
      data: { keywords?: string[] | null; categories?: Tag[] | null }
    }

const VideoFields: React.FC = () => {
  const { toast } = useContext(FeedbackContext)

  const [campaignTargeting, setCampaignTargeting] =
    useState<CampaignTargetingState>()
  const [settingBannerIsValid, setSettingBannerIsValid] = useState(true)

  const [mediaBannerIsValid, setMediaBannerIsValid] = useState(true)
  const [bannersToTable, setBannersToTable] = useState<BannersToTable[]>([])

  const [additionalAssetsIsValid, setAdditionalAssetsIsValid] = useState(false)

  const [budgetValueIsValid, setBudgetValueIsValid] = useState(false)

  const {
    formRef,
    defaultCampaignName,
    rmid,
    costType,
    audienceId,
    setCampaignPeriod,
    handleDebugSubmit,
    additionalAssetsForVideoIsRequired,
    toggleDebugMode
  } = useCampaignForm()
  const { prepareCampaignCreateBody, isLinkedInsertionOrderReady } =
    usePrepareCampaignCreateBody()

  /**
   * Form is ready
   */
  const formIsValid = useMemo(() => {
    const bannerMediaIsValid = !!(
      !!mediaBannerIsValid && !!bannersToTable?.length
    )

    const bannerSettingsIsValid = !!settingBannerIsValid

    const bannerSettingIsValidMessage: StringNull = !bannerSettingsIsValid
      ? t('rm:campaign.create.alerts.leastOneSegmentationConfiguration')
      : null

    const hasAtLeastOneBannerMessage: StringNull = !bannerMediaIsValid
      ? t('rm:campaign.create.alerts.leastOneBanner')
      : null

    const additionalAssetsIsValidMessage: StringNull =
      additionalAssetsForVideoIsRequired && !additionalAssetsIsValid
        ? t('rm:campaign.create.alerts.AdditionalAssetIsRequired')
        : null

    const messages = [
      bannerSettingIsValidMessage,
      hasAtLeastOneBannerMessage,
      additionalAssetsIsValidMessage
    ].filter(item => !!item)

    return {
      isValid: bannerMediaIsValid && bannerSettingsIsValid,
      messages
    }
  }, [
    mediaBannerIsValid,
    bannersToTable?.length,
    settingBannerIsValid,
    additionalAssetsForVideoIsRequired,
    additionalAssetsIsValid
  ])

  const formDataFinal = useMemo(() => {
    const getTargeting = () => {
      if (campaignTargeting?.targeting === 'category') {
        return {
          categories: String(
            campaignTargeting?.data?.categories.map(item => item.id)
          )
        }
      }

      if (campaignTargeting?.targeting === 'keyword') {
        return {
          keywords: campaignTargeting?.data?.keywords
        }
      }

      return {}
    }

    return {
      name: 'name',
      start_date: moment().format('YYYY-MM-DD'),
      type: 'on_site',
      banners: bannersToTable,
      targeting: getTargeting()
    }
  }, [bannersToTable, campaignTargeting])

  /**
   * Form is ready
   * Valor para controlarmos se devemos bloquear o submit
   */
  const formIsReadyToSubmit = useMemo(
    () =>
      (costType === 'daily_cost' || budgetValueIsValid) &&
      isLinkedInsertionOrderReady,
    [budgetValueIsValid, costType, isLinkedInsertionOrderReady]
  )

  /**
   * Handle products
   */
  const {
    campaign: {
      products: { selectedProducts }
    }
  } = useAppSelector(state => state.retailMedia)

  /**
   * Handle request
   */
  const { submitErrorMessage, resolveSubmitError } = useSubmitErrorMessage()

  const [AlertErrorCampaign, setErrorCampaign] = useErrorApiAlert({
    title: submitErrorMessage
  })

  const { mutate, isPending } = useMutation({
    mutationKey: [createCampaignKey],
    mutationFn: createCampaignService,
    onSuccess: response => {
      history.push(`/retail-media/campaign/view/${response.data.id}`)

      toast.success(t('rm:campaign.create.toast.success.message'))
    },
    onError: error => {
      setErrorCampaign(error)

      handleValidationsErrorsAPI(error, formRef)

      toast.critical(resolveSubmitError(error))
    }
  })

  /**
   * Handle submit
   */
  const { lengthCentsCurrency } = useAppInfo()

  const handleSubmit = useCallback(
    async (formData: CreateCampaignBannerVideoFormData) => {
      const preData = {
        ...formData,
        products: selectedProducts,
        banners: bannersToTable,
        publisher_id: rmid,
        lengthCentsCurrency
      }

      const body = DTOCreateBannerVideoCampaign(preData)
      const action = handleDebugSubmit || mutate

      applyPreparedCampaignBody(body, prepareCampaignCreateBody, action)
    },
    [
      selectedProducts,
      bannersToTable,
      rmid,
      lengthCentsCurrency,
      handleDebugSubmit,
      mutate,
      prepareCampaignCreateBody
    ]
  )

  /**
   * Initial data
   */
  const initialData: Partial<InitialDataBannerVideo> = {
    type: 'banner_video',
    name: defaultCampaignName,
    start_date: moment().format('YYYY-MM-DD'),
    budget: { daily: null, cpc: null, cpm: null },
    placements: { placementsCheck: 'all' },
    audienceToggleWrapper: { audienceToggle: !!audienceId },
    settings: withDefaultAgeRestrictedSetting({
      budget_type: 'fast_as_possible'
    }),
    cost: {
      cost_type: 'cpmcpc'
    },
    subpublishers: {
      subpublishersCheck: 'all'
    }
  }

  return (
    <Form ref={formRef} onSubmit={handleSubmit} initialData={initialData}>
      <CampaignType />

      <NameAndTimeInterval
        formRef={formRef}
        labelTitle={t('rm:campaign.create.NameAndTimeInterval.labelTitle')}
        placeholderTitle={t(
          'rm:campaign.create.NameAndTimeInterval.placeholderTitle'
        )}
        hasDescription
        labelDescription={t(
          'rm:campaign.create.NameAndTimeInterval.labelDescription'
        )}
        placeholderDescription={t(
          'rm:campaign.create.NameAndTimeInterval.placeholderDescription'
        )}
        onlyDates
        endDateIsRequired={false}
        showValidBadgeOnSection
        intervalAlertMessage={[
          t('rm:campaign.create.NameAndTimeInterval.intervalAlertMessage')
        ]}
        showEndDateOptional
        titlePanel={t('rm:campaign.create.NameAndTimeInterval.titlePanel')}
        onChangeDate={setCampaignPeriod}
      />

      <Subpublishers />

      <CampaignTargeting
        handleSectionValidity={setSettingBannerIsValid}
        onChange={setCampaignTargeting}
        formRef={formRef}
      />

      <Scope path="banner">
        <Banner
          type="banner_video"
          initialData={{ publisher_id: rmid }}
          formRef={formRef}
          bannersToTable={bannersToTable}
          setBannersToTable={setBannersToTable}
          handleSectionValidity={setMediaBannerIsValid}
          selectedSegmentation={campaignTargeting?.targeting}
        />
      </Scope>

      {additionalAssetsForVideoIsRequired && (
        <AdditionalAssets handleSectionValidity={setAdditionalAssetsIsValid} />
      )}

      {/* No isRequired here on purpose: that flag exists only for the
          offsite briefing flow, where the campaign-wide URL becomes
          mandatory once some accepted media has none of its own. Onsite
          has no such fallback rule, so this panel is never forced. */}
      <CampaignDestinationUrl />

      <ProductsPanel
        formRef={formRef}
        description={t('rm:campaign.create.ProductsPanel.description.relate')}
        publisherId={rmid as PublisherId}
      />

      {!!formIsValid.messages?.length && (
        <section className="WrapperAlerts d-flex flex-column gap-2 mb-3">
          {formIsValid.messages.map((message, index) => (
            <Alert
              variant="warning"
              key={index}
              onClick={message?.action}
              role={message?.action ? 'button' : 'text'}
            >
              {message?.text || message}
            </Alert>
          ))}
        </section>
      )}

      <hr />

      {costType === 'cpmcpc' && (
        <Budget
          adType="banner_video"
          formRef={formRef}
          publisherId={rmid}
          formData={formDataFinal}
          formIsValid={formIsValid}
          handleIsValid={setBudgetValueIsValid}
          initialValueDailyBudget={'R$ 100.00'}
        />
      )}

      {costType === 'daily_cost' && <DailyCostScope />}

      <AdvancedSettings targeting={campaignTargeting?.targeting} />

      <AlertErrorCampaign />

      {!!handleDebugSubmit && (
        <Alert variant="critical">
          🚀 ~ DEBUG MODE ATIVADO: este modo imprime o payload da requisição no
          console e desabilita o envio do formulário.
        </Alert>
      )}

      <div className="form-default__buttons">
        {!!handleDebugSubmit && (
          <Button
            type="button"
            text={'Desativar modo debug'}
            onClick={toggleDebugMode}
            size="large"
            template="critical"
          />
        )}

        <Button
          type="submit"
          text={t('rm:sendForApproval')}
          loading={isPending}
          size="large"
          template="primary"
          disabled={!formIsReadyToSubmit}
        />
      </div>
    </Form>
  )
}

export default VideoFields
